import z from "zod";
import type { AccountManagerAssignmentBlockedReason } from "@remi/types/account-manager-assignment";
import type { PhoneProvider } from "@remi/types/phoneProvider";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

export const LifecycleHeldSendReasonValues = [
  "NO_ACCOUNT_MANAGER",
  "ACCOUNT_MANAGER_HAS_NO_LINE",
  "NO_LIVE_CANONICAL_BIDS_NO_AM",
  "NO_ACTIVE_BID_NO_AM",
] as const;
export type LifecycleHeldSendReason = (typeof LifecycleHeldSendReasonValues)[number];

const communicationsErrorTypeValues = [
  "UNKNOWN",
  "BID_NOT_FOUND",
  "BID_REQUEST_NOT_FOUND",
  "PROPERTY_ADDRESS_NOT_FOUND",
  "PROJECT_NOT_FOUND",
  "PARTNER_ORG_NOT_FOUND",
  "PARTNER_UPDATE_CHANNEL_UNRESOLVED",
  "PARTNER_UPDATE_CONVERSATION_NOT_IN_COHORT",
  "GROOVE_SWEEP_BLAST_RADIUS_EXCEEDED",
  "GROOVE_SWEEP_PARTIAL_FAILURE",
  "OPEN_PHONE_RECONCILIATION_BACKFILL_REFUSED",
  "REQUEST_FOR_QUOTE_NOT_FOUND",
  "ACTIVE_QUOTE_NOT_FOUND",
  "CONVERSATION_NOT_FOUND",
  "CONVERSATION_PLACEMENT_BATCH_TOO_LARGE",
  "CONVERSATION_BIRTH_TEAM_NOT_ASSIGNABLE",
  "NO_ACTIVE_TASK_CONVERSATION",
  "CONVERSATION_CREATION_RACE",
  "CONVERSATION_ITEM_CREATION_RACE",
  "MESSAGE_CREATION_RACE",
  "TICKET_NOT_FOUND",
  "PROJECT_BID_NOT_FOUND",
  "NO_REACTIONS_FOUND_FOR_MESSAGE",
  "MESSAGE_NOT_IN_SOLAR_COMPANY_THREAD",
  "MESSAGE_NOT_FOUND",
  "MESSAGE_DELETE_FORBIDDEN",
  "MESSAGE_EDIT_FORBIDDEN",
  "NOTIFICATION_NOT_FOUND",
  "NOTIFICATION_EVENT_NOT_FOUND",
  "PHONE_NUMBER_NOT_FOUND",
  "SPECIALIST_LINE_OWNERSHIP_CONFLICT",
  "INTERNAL_LINE_OWNERSHIP_CONFLICT",
  "PHONE_NOT_FOUND",
  "PROXY_PHONE_NOT_FOUND",
  "PHONE_CALL_NOT_FOUND",
  "PHONE_CALL_ID_NOT_FOUND",
  "OPENPHONE_PHONE_CALL_NOT_FOUND_FOR_TRANSCRIPT",
  "TRANSCRIPT_REDACTION_SEGMENT_MISSING",
  "PHONE_CALL_FAILED_TO_PARSE",
  "UNATTACHED_PROXY_PHONE",
  "NO_AVAILABLE_PROXY_PHONES",
  "PROXY_ASSIGNMENT_ATTEMPT_FAILED",
  "PROXY_ASSIGNMENT_HOMEOWNER_SOURCE",
  "PROXY_ASSIGNMENT_ORG_UNRESOLVED",
  "PROXY_ASSIGNMENT_NO_ORG_MATCH",
  "PROXY_ASSIGNMENT_HOMEOWNER_PHONE_NOT_FOUND",
  "INBOX_VIEW_NOT_FOUND",
  "USER_NOT_ALLOWED_TO_UPDATE_INBOX_VIEW",
  "USER_NOT_ALLOWED_TO_DELETE_INBOX_VIEW",
  "ORGANIZATION_NOT_FOUND",
  "CONVERSATION_ITEM_NOT_FOUND",
  "CONVERSATION_ITEM_WRONG_TYPE",
  "CONVERSATION_ITEM_INVALID_STATE",
  "CONVERSATION_ITEM_NOT_REPLYABLE",
  "CONVERSATION_ITEM_NOT_REACTABLE",
  "CONVERSATION_WRONG_TYPE",
  "RELATED_CONVERSATIONS_NOT_IMPLEMENTED_FOR_TYPE",
  "INVALID_PHONE_PROVIDER",
  "UNKNOWN_NOTIFICATION_TYPE",
  "WEB_PUSH_NOT_CONFIGURED",
  "PROJECT_MISSING_PARTNER_FLOW",
  "UNABLE_TO_DETERMINE_AI_SUMMARY_VERSION",
  "WORKSITE_NOT_FOUND",
  "CANNOT_DEACTIVATE_PROXY_PHONE_WITH_PHONE",
  "CANNOT_DEACTIVATE_PROXY_PHONE_WITH_ACTIVE_ASSIGNMENT",
  "CANNOT_ASSIGN_DEDICATED_PROXY_TO_NON_CONTRACTOR_PHONE",
  "CANNOT_ASSIGN_DEDICATED_PROXY_TO_SHARED_PHONE",
  "PROXY_PHONE_ALREADY_SERVES_ASSIGNMENTS",
  "CANNOT_UNASSIGN_PROXY_PHONE_WITH_ACTIVE_ASSIGNMENT",
  "DEDICATED_PROXY_ASSIGNMENT_LOST_RACE",
  "PROXY_PHONE_ASSIGNMENT_HOMEOWNER_CHANGED",
  "PROXY_PHONE_ASSIGNMENT_ALREADY_EXISTS_FOR_ORG",
  "CONTRACTOR_ALREADY_REACHES_HOMEOWNER_VIA_OTHER_PROXY",
  "CANNOT_CREATE_ASSIGNMENT_ON_DEDICATED_PROXY",
  "CANNOT_CREATE_ASSIGNMENT_FOR_DEDICATED_PHONE",
  "PROXY_PHONE_ASSIGNMENT_RACED",
  "PROXY_PHONE_ASSIGNMENT_NOT_RELEASED",
  "PROXY_END_OF_CONVERSATION_UNAVAILABLE",
  "PROXY_RELEASE_COUNT_MISMATCH",
  "STALE_PROXY_RELEASE_COUNT_MISMATCH",
  "TOO_MANY_ASSIGNMENTS_TO_RELEASE_AT_ONCE",
  "PROXY_PHONE_DEACTIVATED_WITH_RACED_ASSIGNMENT",
  "GROUP_DETECTED_IN_PROXY_CONVERSATION",
  "TEAM_NOT_FOUND",
  "FAILED_TO_SYNC_SMS_MESSAGES",
  "FAILED_TO_CREATE_AI_SUMMARY_MESSAGE",
  "NO_BID_FOUND_FOR_ADDRESS",
  "EMAIL_ADDRESS_NOT_FOUND",
  "INVALID_CONVERSATION_KEY",
  "INVALID_ORGANIZATION_TYPES_FOR_AUTOMATED_MESSAGE",
  "CANNOT_BUY_PHONE_NUMBER_IN_NON_PRODUCTION_ENVIRONMENT",
  "NO_SUNRUN_RECORD_ID_FOUND_FOR_WORK_ORDER",
  "PROXY_PHONE_NUMBER_USED_AS_CONTACT",
  "SYSTEM_PHONE_NUMBER_USED_AS_CONTACT",
  "TELECOM_USER_ALREADY_MAPPED",
  "TELECOM_PERSON_EMAIL_ALREADY_EXISTS",
  "EMAIL_OBJECT_BUCKET_NOT_CONFIGURED",
  "WEBHOOK_OWNERS_FILTERED",
  "USER_HAS_NO_CONVERSATION_ON_BID",
  "UNAUTHORIZED_CHANNEL_CALLER",
  "PARTNER_MENTION_UNAUTHORIZED_SENDER",
  "PARTNER_MENTION_PROJECT_CONVERSATION_MISMATCH",
  "PORTAL_COLOR_SELECTION_DISABLED",
  "XA_FILE_DELIVERY_INVALID_INPUT",
  "XA_FILE_DELIVERY_BID_UNRESOLVED",
  "WESTHILL_COMPLETION_SUMMARY_INVALID_INPUT",
  "WESTHILL_COMPLETION_SUMMARY_BID_UNRESOLVED",
  "WESTHILL_COMPLETION_SUMMARY_PLACEHOLDER_LEAK",
  "CONVERSATION_TEAM_INVALID",
  "CONVERSATION_UNASSIGN_NOT_PERMITTED",
  "ACCOUNT_MANAGER_STAFFING_BID_MISSING",
  "CONVERSATION_OWNERSHIP_NOT_MANAGED",
  "ESCALATION_SEAT_ASSUMPTION_ON_LIVE_RUN",
  "CONVERSATION_OWNERSHIP_SWEEP_CURSOR_STALLED",
  "AUTOMATED_MENTION_TEAM_NOT_MENTIONABLE",
  "AUTOMATED_MENTION_LEAD_NOT_FOUND",
  "CS_LINE_NOT_SENDABLE",
  "AUTOMATED_SMS_SEND_FAILED",
  "INBOX_CURSOR_INVALID",
  "INBOX_BULK_TARGET_INVALID",
  "INBOX_BULK_ROW_REJECTED",
  "LIFECYCLE_SENDER_HELD_SEND",
  "ACCOUNT_MANAGER_SENDER_UNRESOLVED",
  "RECIPIENT_PHONE_NOT_ON_BID",
  "INTRO_ROW_NOT_SCHEDULED",
  "AUTOMATED_SMS_ROW_NOT_SCHEDULED",
  "INBOX_SEARCH_TIMEOUT",
  "INBOX_SEARCH_INPUT_EMPTY",
  "CONVERSATION_QUIET_QUERY_TIMEOUT",
  "CONVERSATION_QUIET_CURSOR_INVALID",
  "CONVERSATION_UNANSWERED_CURSOR_INVALID",
  "CONVERSATION_ITEMS_CURSOR_INVALID",
  "CONVERSATION_HISTORY_CURSOR_INVALID",
  "TELECOM_PHONE_NUMBER_NOT_ACTIVE",
  "TELECOM_PERSON_NOT_FOUND",
  "TELECOM_PERSON_NOT_INTERNAL",
  "TELECOM_PERSON_MISSING_CONTACT",
  "TELECOM_PERSON_ALREADY_HAS_LINE",
  "TELECOM_SEAT_OWNED_BY_OTHER",
  "TELECOM_USER_NOT_ON_NUMBER",
  "TELECOM_OPEN_PHONE_UNAVAILABLE",
  "TELECOM_LINE_TEAM_REQUIRED",
  "TELECOM_LINE_TEAM_MEMBERSHIP_REQUIRED",
  "TELECOM_LINE_TEAM_NOT_ASSIGNABLE",
  "TELECOM_LINE_DUPLICATE_ROWS",
  "TELECOM_LINE_PLACEHOLDER_REPAIR_REFUSED",
  "INBOX_COUNTS_UNSUPPORTED_ASSIGNEE_SHAPE",
  "INBOX_COUNTS_MISSING_ASSIGNEE_ANCHOR",
  "INBOX_COUNTS_HETEROGENEOUS_BATCH",
  "INBOX_FILTER_SELECT_CONFLICT",
  "PRE_BID_CONVERSATION_LINK_BID_NOT_FOUND",
  "CANNOT_ASSIGN_PROXY_TO_INTERNAL_LINE",
  "SMS_MESSAGE_NOT_RESCHEDULABLE",
] as const;
const communicationsErrorTypeSchema = z.enum(communicationsErrorTypeValues);
export const CommunicationsErrorType = communicationsErrorTypeSchema.enum;
type CommunicationsErrorType = z.infer<typeof communicationsErrorTypeSchema>;

export type CommunicationsErrors =
  | { errorType: typeof CommunicationsErrorType.UNKNOWN; data: undefined }
  | { errorType: typeof CommunicationsErrorType.BID_NOT_FOUND; data: { bidId?: number; projectId?: number } }
  | {
      errorType: typeof CommunicationsErrorType.BID_REQUEST_NOT_FOUND;
      data: { externalId: string; buyerOrganizationId: string };
    }
  | { errorType: typeof CommunicationsErrorType.PROPERTY_ADDRESS_NOT_FOUND; data: { bidId: number } }
  | { errorType: typeof CommunicationsErrorType.PROJECT_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof CommunicationsErrorType.PARTNER_ORG_NOT_FOUND; data: { projectId: number; bidId: number } }
  | {
      errorType: typeof CommunicationsErrorType.PARTNER_UPDATE_CONVERSATION_NOT_IN_COHORT;
      data: { conversationId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.PARTNER_UPDATE_CHANNEL_UNRESOLVED;
      data: {
        conversationId: string;
        channel: string;
        reason: "MISSING_EXTERNAL_IDENTIFIERS" | "CHANNEL_NOT_PARTNER_FACING";
      };
    }
  | {
      errorType: typeof CommunicationsErrorType.GROOVE_SWEEP_BLAST_RADIUS_EXCEEDED;
      data: { mailboxName: string; proposedCloses: number; openTickets: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.GROOVE_SWEEP_PARTIAL_FAILURE;
      data: { closeFailures: number; assignFailures: number; resolutionFailures: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.OPEN_PHONE_RECONCILIATION_BACKFILL_REFUSED;
      data: {
        reason: "gates-invalid" | "classification-errors" | "count-mismatch" | "error-rate";
        planned: number | null;
        expected: number | null;
        errored: number | null;
        raw: string | null;
      };
    }
  | {
      errorType: typeof CommunicationsErrorType.REQUEST_FOR_QUOTE_NOT_FOUND;
      data: { projectId: number; bidId: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.ACTIVE_QUOTE_NOT_FOUND;
      data: { projectId: number; bidId: number; requestForQuoteId: number };
    }
  | { errorType: typeof CommunicationsErrorType.CONVERSATION_NOT_FOUND; data: { id: string } }
  | {
      errorType: typeof CommunicationsErrorType.CONVERSATION_PLACEMENT_BATCH_TOO_LARGE;
      data: { count: number; max: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.CONVERSATION_BIRTH_TEAM_NOT_ASSIGNABLE;
      data: { conversationId: string; teamId: string | null };
    }
  | {
      errorType: typeof CommunicationsErrorType.CONVERSATION_UNASSIGN_NOT_PERMITTED;
      data: { id: string; expectedAssigneeId: string; teamId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.ACCOUNT_MANAGER_STAFFING_BID_MISSING;
      data: { conversationId: string; bidProjectId: number };
    }
  | { errorType: typeof CommunicationsErrorType.CONVERSATION_OWNERSHIP_NOT_MANAGED; data: { id: string } }
  | { errorType: typeof CommunicationsErrorType.ESCALATION_SEAT_ASSUMPTION_ON_LIVE_RUN; data: { workSiteId: number } }
  | {
      errorType: typeof CommunicationsErrorType.CONVERSATION_OWNERSHIP_SWEEP_CURSOR_STALLED;
      data: { channel: string; afterId: string | null; pageCount: number };
    }
  | { errorType: typeof CommunicationsErrorType.NO_ACTIVE_TASK_CONVERSATION; data: { taskId: string } }
  | { errorType: typeof CommunicationsErrorType.CONVERSATION_CREATION_RACE; data: { id: string } }
  | {
      errorType: typeof CommunicationsErrorType.CONVERSATION_ITEM_CREATION_RACE;
      data: { id: string | null };
    }
  | {
      errorType: typeof CommunicationsErrorType.MESSAGE_CREATION_RACE;
      data: { conversationItemId: string };
    }
  | { errorType: typeof CommunicationsErrorType.TICKET_NOT_FOUND; data: { ticketId: string } }
  | { errorType: typeof CommunicationsErrorType.PROJECT_BID_NOT_FOUND; data: { projectId: number } }
  | {
      errorType: typeof CommunicationsErrorType.NO_REACTIONS_FOUND_FOR_MESSAGE;
      data: { messageId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.MESSAGE_NOT_IN_SOLAR_COMPANY_THREAD;
      data: { messageId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.MESSAGE_NOT_FOUND;
      data: { messageId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.MESSAGE_DELETE_FORBIDDEN;
      data: { messageId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.MESSAGE_EDIT_FORBIDDEN;
      data: { messageId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.NOTIFICATION_NOT_FOUND;
      data: { notificationId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.NOTIFICATION_EVENT_NOT_FOUND;
      data: { eventId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.INBOX_VIEW_NOT_FOUND;
      data: { inboxViewId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.USER_NOT_ALLOWED_TO_UPDATE_INBOX_VIEW;
      data: { inboxViewId: string; personId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.USER_NOT_ALLOWED_TO_DELETE_INBOX_VIEW;
      data: { inboxViewId: string; personId: string };
    }
  | { errorType: typeof CommunicationsErrorType.BID_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof CommunicationsErrorType.PHONE_NUMBER_NOT_FOUND; data: { body: unknown } }
  | {
      errorType: typeof CommunicationsErrorType.SPECIALIST_LINE_OWNERSHIP_CONFLICT;
      data:
        | { conflict: "MULTIPLE_ACTIVE_OWNERS_FOR_LINE"; phoneNumber: string; ownerIds: string[] }
        | { conflict: "PERSON_OWNS_MULTIPLE_LINES"; personId: string; openPhoneLineCount: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.INTERNAL_LINE_OWNERSHIP_CONFLICT;
      data: { fromPhoneNumber: string; internalOwnerIds: string[]; disabledInternalOwnerIds: string[] };
    }
  | {
      errorType: typeof CommunicationsErrorType.PHONE_NOT_FOUND;
      data: { phoneNumber: string; conversationId?: string };
    }
  | { errorType: typeof CommunicationsErrorType.PROXY_PHONE_NOT_FOUND; data: { proxyPhoneNumber: string } }
  | { errorType: typeof CommunicationsErrorType.PHONE_CALL_NOT_FOUND; data: { externalId: string; provider: string } }
  | { errorType: typeof CommunicationsErrorType.PHONE_CALL_ID_NOT_FOUND; data: { phoneCallId: string } }
  | {
      errorType: typeof CommunicationsErrorType.OPENPHONE_PHONE_CALL_NOT_FOUND_FOR_TRANSCRIPT;
      data: { externalId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.TRANSCRIPT_REDACTION_SEGMENT_MISSING;
      data: { phoneCallId: string; segmentId: string };
    }
  | { errorType: typeof CommunicationsErrorType.PHONE_CALL_FAILED_TO_PARSE; data: { phoneCallResponse: unknown } }
  | {
      errorType: typeof CommunicationsErrorType.UNATTACHED_PROXY_PHONE;
      data: { proxyPhoneNumber: string; sourceNumber: string };
    }
  | { errorType: typeof CommunicationsErrorType.NO_AVAILABLE_PROXY_PHONES; data: undefined }
  | {
      errorType: typeof CommunicationsErrorType.PROXY_ASSIGNMENT_ATTEMPT_FAILED;
      data: { phoneId: string; areaCode?: number | string; provider?: string; error?: string };
    }
  | {
      errorType:
        | typeof CommunicationsErrorType.PROXY_ASSIGNMENT_HOMEOWNER_SOURCE
        | typeof CommunicationsErrorType.PROXY_ASSIGNMENT_ORG_UNRESOLVED
        | typeof CommunicationsErrorType.PROXY_ASSIGNMENT_NO_ORG_MATCH;
      data: {
        proxyPhoneId: string;
        reason: "HOMEOWNER_SOURCE" | "UNRESOLVED_ORG" | "NO_ASSIGNMENT_FOR_ORG";
      };
    }
  | {
      errorType: typeof CommunicationsErrorType.PROXY_ASSIGNMENT_HOMEOWNER_PHONE_NOT_FOUND;
      data: { proxyPhoneId: string; homeownerPhoneId: string };
    }
  | { errorType: typeof CommunicationsErrorType.ORGANIZATION_NOT_FOUND; data: { organizationId: string } }
  | { errorType: typeof CommunicationsErrorType.NO_AVAILABLE_PROXY_PHONES; data: undefined }
  | { errorType: typeof CommunicationsErrorType.NO_AVAILABLE_PROXY_PHONES; data: undefined }
  | { errorType: typeof CommunicationsErrorType.CONVERSATION_ITEM_NOT_FOUND; data: { conversationItemId: string } }
  | { errorType: typeof CommunicationsErrorType.CONVERSATION_ITEM_WRONG_TYPE; data: { conversationItemId: string } }
  | { errorType: typeof CommunicationsErrorType.CONVERSATION_ITEM_INVALID_STATE; data: { conversationItemId: string } }
  | { errorType: typeof CommunicationsErrorType.CONVERSATION_ITEM_NOT_REPLYABLE; data: { conversationItemId: string } }
  | { errorType: typeof CommunicationsErrorType.CONVERSATION_ITEM_NOT_REACTABLE; data: { conversationItemId: string } }
  | {
      errorType: typeof CommunicationsErrorType.CONVERSATION_WRONG_TYPE;
      data: { id: string; type: string; expectedType: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.RELATED_CONVERSATIONS_NOT_IMPLEMENTED_FOR_TYPE;
      data: { id: string; type: string };
    }
  | { errorType: typeof CommunicationsErrorType.INVALID_PHONE_PROVIDER; data: { provider: string } }
  | { errorType: typeof CommunicationsErrorType.UNKNOWN_NOTIFICATION_TYPE; data: { notificationType: string } }
  | { errorType: typeof CommunicationsErrorType.WEB_PUSH_NOT_CONFIGURED; data: { reason: string } }
  | { errorType: typeof CommunicationsErrorType.PROJECT_MISSING_PARTNER_FLOW; data: { projectId: number } }
  | { errorType: typeof CommunicationsErrorType.UNABLE_TO_DETERMINE_AI_SUMMARY_VERSION; data: { summaryId: string } }
  | { errorType: typeof CommunicationsErrorType.WORKSITE_NOT_FOUND; data: { workSiteId: number } }
  | { errorType: typeof CommunicationsErrorType.INVALID_PHONE_PROVIDER; data: { provider: string } }
  | {
      errorType: typeof CommunicationsErrorType.CANNOT_DEACTIVATE_PROXY_PHONE_WITH_PHONE;
      data: { proxyPhoneNumber: string; proxyPhoneId: string; phoneId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.CANNOT_DEACTIVATE_PROXY_PHONE_WITH_ACTIVE_ASSIGNMENT;
      data: { proxyPhoneNumber: string; proxyPhoneId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.CANNOT_ASSIGN_DEDICATED_PROXY_TO_NON_CONTRACTOR_PHONE;
      data: { phoneId: string; phoneProxyRole: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.CANNOT_ASSIGN_DEDICATED_PROXY_TO_SHARED_PHONE;
      data: { phoneId: string; activeAssignmentCount: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.PROXY_PHONE_ALREADY_SERVES_ASSIGNMENTS;
      data: { proxyPhoneId: string; activeAssignmentCount: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.CANNOT_UNASSIGN_PROXY_PHONE_WITH_ACTIVE_ASSIGNMENT;
      data: { phoneId: string; activeAssignmentCount: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.DEDICATED_PROXY_ASSIGNMENT_LOST_RACE;
      data: { phoneId: string; proxyPhoneId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.PROXY_PHONE_ASSIGNMENT_HOMEOWNER_CHANGED;
      data: {
        proxyPhoneId: string;
        contractorOrgId: string;
        expectedHomeownerPhoneId: string;
        actualHomeownerPhoneId: string;
      };
    }
  | {
      errorType: typeof CommunicationsErrorType.PROXY_PHONE_ASSIGNMENT_ALREADY_EXISTS_FOR_ORG;
      data: {
        proxyPhoneId: string;
        contractorOrgId: string;
        requestedHomeownerPhoneId: string;
        currentHomeownerPhoneId: string;
      };
    }
  | {
      errorType: typeof CommunicationsErrorType.CONTRACTOR_ALREADY_REACHES_HOMEOWNER_VIA_OTHER_PROXY;
      data: {
        contractorOrgId: string;
        homeownerPhoneId: string;
        requestedProxyPhoneId: string;
        actualProxyPhoneId: string;
      };
    }
  | {
      errorType: typeof CommunicationsErrorType.CANNOT_CREATE_ASSIGNMENT_ON_DEDICATED_PROXY;
      data: { proxyPhoneId: string; boundPhoneId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.CANNOT_CREATE_ASSIGNMENT_FOR_DEDICATED_PHONE;
      data: { homeownerPhoneId: string; dedicatedProxyPhoneId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.PROXY_PHONE_ASSIGNMENT_RACED;
      data: { proxyPhoneId: string; contractorOrgId: string; homeownerPhoneId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.PROXY_PHONE_ASSIGNMENT_NOT_RELEASED;
      data: { proxyPhoneId: string; contractorOrgId: string; homeownerPhoneId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.PROXY_END_OF_CONVERSATION_UNAVAILABLE;
      data: {
        proxyPhoneId: string;
        contractorOrgId: string;
        homeownerPhoneId: string;
        sendEndOfConversationText?: boolean;
        unmetCondition: string;
      };
    }
  | {
      errorType: typeof CommunicationsErrorType.PROXY_RELEASE_COUNT_MISMATCH;
      data: {
        bidId: number;
        projectId: number;
        expectedReleaseCount: number;
        actualReleaseCount: number;
        contractorOrgIds: string[];
      };
    }
  | {
      errorType: typeof CommunicationsErrorType.STALE_PROXY_RELEASE_COUNT_MISMATCH;
      data: {
        phoneId: string;
        proxyPhoneId: string;
        expectedReleaseCount: number;
        actualReleaseCount: number;
        contractorOrgIds: string[];
      };
    }
  | {
      errorType: typeof CommunicationsErrorType.TOO_MANY_ASSIGNMENTS_TO_RELEASE_AT_ONCE;
      data: { organizationId: string; scanLimit: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.PROXY_PHONE_DEACTIVATED_WITH_RACED_ASSIGNMENT;
      data: { proxyPhoneNumber: string; proxyPhoneId: string; racedAssignmentCount: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.GROUP_DETECTED_IN_PROXY_CONVERSATION;
      data: { phoneNumbers: string[]; conversationId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.TEAM_NOT_FOUND;
      data: { teamId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.FAILED_TO_SYNC_SMS_MESSAGES;
      data: { errorCount: number; successCount: number; provider: PhoneProvider };
    }
  | {
      errorType: typeof CommunicationsErrorType.FAILED_TO_CREATE_AI_SUMMARY_MESSAGE;
      data: { bidId: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.NO_BID_FOUND_FOR_ADDRESS;
      data: { address: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.EMAIL_ADDRESS_NOT_FOUND;
      data: { personId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.INVALID_CONVERSATION_KEY;
      data: { key: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.INVALID_ORGANIZATION_TYPES_FOR_AUTOMATED_MESSAGE;
      data: { orgTypes: string[] };
    }
  | {
      errorType: typeof CommunicationsErrorType.CANNOT_BUY_PHONE_NUMBER_IN_NON_PRODUCTION_ENVIRONMENT;
      data: undefined;
    }
  | {
      errorType: typeof CommunicationsErrorType.NO_SUNRUN_RECORD_ID_FOUND_FOR_WORK_ORDER;
      data: { workOrderId: string; messageId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.PROXY_PHONE_NUMBER_USED_AS_CONTACT;
      data: { phoneNumber: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.SYSTEM_PHONE_NUMBER_USED_AS_CONTACT;
      data: { phoneNumber: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.TELECOM_USER_ALREADY_MAPPED;
      data: { telecomProviderUserId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.TELECOM_PERSON_EMAIL_ALREADY_EXISTS;
      data: { email: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.EMAIL_OBJECT_BUCKET_NOT_CONFIGURED;
      data: { variable: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.WEBHOOK_OWNERS_FILTERED;
      data: { conversationItemId: string; droppedPersonIds: string[] };
    }
  | {
      errorType: typeof CommunicationsErrorType.USER_HAS_NO_CONVERSATION_ON_BID;
      data: {
        bidId: number;
        partnerFlow: string | null;
        userOrganizationId: string;
        userOrganizationType: string;
      };
    }
  | {
      errorType: typeof CommunicationsErrorType.UNAUTHORIZED_CHANNEL_CALLER;
      data: { callerOrgId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.PARTNER_MENTION_UNAUTHORIZED_SENDER;
      data: { senderOrgId: string; conversationId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.PARTNER_MENTION_PROJECT_CONVERSATION_MISMATCH;
      data: { projectId: number; conversationId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.PORTAL_COLOR_SELECTION_DISABLED;
      data: { projectId: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.XA_FILE_DELIVERY_INVALID_INPUT;
      data: { fileId: string; hasDeliveringOrganizationId: boolean; hasExternalRef: boolean };
    }
  | {
      errorType: typeof CommunicationsErrorType.XA_FILE_DELIVERY_BID_UNRESOLVED;
      data: { fileId: string; externalRef: string; deliveringOrganizationId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.WESTHILL_COMPLETION_SUMMARY_INVALID_INPUT;
      data: { fileId: string; hasDeliveringOrganizationId: boolean; hasExternalRef: boolean };
    }
  | {
      errorType: typeof CommunicationsErrorType.WESTHILL_COMPLETION_SUMMARY_BID_UNRESOLVED;
      data: { fileId: string; externalRef: string; deliveringOrganizationId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.WESTHILL_COMPLETION_SUMMARY_PLACEHOLDER_LEAK;
      data: { externalRef: string; partnerFlow: string; projectId: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.CONVERSATION_TEAM_INVALID;
      data: { teamId: string | null };
    }
  | {
      errorType: typeof CommunicationsErrorType.AUTOMATED_MENTION_TEAM_NOT_MENTIONABLE;
      data: { teamKey: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.AUTOMATED_MENTION_LEAD_NOT_FOUND;
      data: { leadId: string; teamKey: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.CS_LINE_NOT_SENDABLE;
      data: { reason: "no_provider"; phoneId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.AUTOMATED_SMS_SEND_FAILED;
      data: {
        smsId: string;
        fromPhoneId: string;
        provider: string;
        smsError?: string;
        ownerId?: string;
        httpStatus?: number;
        providerCode?: string;
      };
    }
  | {
      errorType: typeof CommunicationsErrorType.INBOX_CURSOR_INVALID;
      data: undefined;
    }
  | {
      errorType: typeof CommunicationsErrorType.INBOX_BULK_TARGET_INVALID;
      data: { assigneeId: string } | { teamId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.INBOX_BULK_ROW_REJECTED;
      data: { reason: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.LIFECYCLE_SENDER_HELD_SEND;
      data: {
        workSiteId: number;
        projectId: number;
        automatedMessageType: string;
        heldSendReason: LifecycleHeldSendReason;
      };
    }
  | {
      errorType: typeof CommunicationsErrorType.ACCOUNT_MANAGER_SENDER_UNRESOLVED;
      data: {
        bidId: number;
        workSiteId: number;
        reason: "NO_ACCOUNT_MANAGER" | "ACCOUNT_MANAGER_HAS_NO_LINE" | AccountManagerAssignmentBlockedReason;
      };
    }
  | {
      errorType: typeof CommunicationsErrorType.RECIPIENT_PHONE_NOT_ON_BID;
      data: { bidId: number; phoneId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.INTRO_ROW_NOT_SCHEDULED;
      data: { smsId: string; status: string; bidProjectId: number; workSiteId: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.AUTOMATED_SMS_ROW_NOT_SCHEDULED;
      data: { smsId: string; status: string; bidProjectId: number; workSiteId: number };
    }
  | {
      errorType: typeof CommunicationsErrorType.INBOX_SEARCH_TIMEOUT;
      data: { callSite: string; searchLength: number; filtersProjectCategory: boolean };
    }
  | {
      errorType: typeof CommunicationsErrorType.INBOX_SEARCH_INPUT_EMPTY;
      data: { callSite: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.CONVERSATION_QUIET_QUERY_TIMEOUT;
      data: { quietForDays: number; take: number; timeoutCode: string };
    }
  | { errorType: typeof CommunicationsErrorType.CONVERSATION_QUIET_CURSOR_INVALID; data: undefined }
  | { errorType: typeof CommunicationsErrorType.CONVERSATION_UNANSWERED_CURSOR_INVALID; data: undefined }
  | { errorType: typeof CommunicationsErrorType.CONVERSATION_ITEMS_CURSOR_INVALID; data: undefined }
  | { errorType: typeof CommunicationsErrorType.CONVERSATION_HISTORY_CURSOR_INVALID; data: undefined }
  | { errorType: typeof CommunicationsErrorType.TELECOM_PHONE_NUMBER_NOT_ACTIVE; data: undefined }
  | { errorType: typeof CommunicationsErrorType.TELECOM_PERSON_NOT_FOUND; data: { personId: string } }
  | {
      errorType: typeof CommunicationsErrorType.TELECOM_PERSON_NOT_INTERNAL;
      data: { personId: string; organizationId: string };
    }
  | { errorType: typeof CommunicationsErrorType.TELECOM_PERSON_MISSING_CONTACT; data: { personId: string } }
  | {
      errorType: typeof CommunicationsErrorType.TELECOM_PERSON_ALREADY_HAS_LINE;
      data: { personId: string; existingLineId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.TELECOM_SEAT_OWNED_BY_OTHER;
      data: { personId: string; externalId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.TELECOM_USER_NOT_ON_NUMBER;
      data: { externalId: string };
    }
  | { errorType: typeof CommunicationsErrorType.TELECOM_OPEN_PHONE_UNAVAILABLE; data: undefined }
  | {
      errorType: typeof CommunicationsErrorType.TELECOM_LINE_TEAM_REQUIRED;
      data: { telecomProviderPhoneId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.TELECOM_LINE_TEAM_MEMBERSHIP_REQUIRED;
      data: { personId: string; teamId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.TELECOM_LINE_TEAM_NOT_ASSIGNABLE;
      data: { teamId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.TELECOM_LINE_DUPLICATE_ROWS;
      data: { telecomProviderPhoneIdsByLine: string[][] };
    }
  | {
      errorType: typeof CommunicationsErrorType.TELECOM_LINE_PLACEHOLDER_REPAIR_REFUSED;
      data: { check: string; [key: string]: unknown };
    }
  | {
      errorType: typeof CommunicationsErrorType.INBOX_COUNTS_UNSUPPORTED_ASSIGNEE_SHAPE;
      data: { key: string; block: string; comparator?: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.INBOX_COUNTS_MISSING_ASSIGNEE_ANCHOR;
      data: { key: string; block: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.INBOX_FILTER_SELECT_CONFLICT;
      data: { field: string; table: "Ticket" | "Conversation" | "Task" | "Escalation" };
    }
  | {
      errorType: typeof CommunicationsErrorType.INBOX_COUNTS_HETEROGENEOUS_BATCH;
      data: {
        entity: "ticket" | "task" | "conversation" | "escalation" | "batch";
        reason: "joins" | "havingClause" | "needsDistinct" | "signature" | "noDefinitions";
      };
    }
  | { errorType: typeof CommunicationsErrorType.PRE_BID_CONVERSATION_LINK_BID_NOT_FOUND; data: { bidId: number } }
  | {
      errorType: typeof CommunicationsErrorType.CANNOT_ASSIGN_PROXY_TO_INTERNAL_LINE;
      data: { phoneId: string; provider: string; externalId: string };
    }
  | {
      errorType: typeof CommunicationsErrorType.SMS_MESSAGE_NOT_RESCHEDULABLE;
      data: { smsMessageId: string };
    };

export const communicationsErrorDetails: Record<CommunicationsErrorType, ErrorDetails> = {
  UNKNOWN: { classification: ErrorClassification.UNEXPECTED_ERROR, sentryDescription: "Unknown Error" },
  BID_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Bid not found" },
  BID_REQUEST_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Bid request not found",
  },
  PROPERTY_ADDRESS_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Property address not found",
  },
  PROJECT_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Project not found" },
  PARTNER_ORG_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Partner org not found for homeowner-responsible amounts message",
  },
  PARTNER_UPDATE_CONVERSATION_NOT_IN_COHORT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Partner update draft requested for a conversation outside the cohort",
  },
  PARTNER_UPDATE_CHANNEL_UNRESOLVED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Partner update resolved no automated-message channel matching the drafted conversation",
  },
  GROOVE_SWEEP_BLAST_RADIUS_EXCEEDED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Groove inbox sweep proposed more closes than the per-run blast-radius cap allows",
  },
  GROOVE_SWEEP_PARTIAL_FAILURE: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Groove inbox sweep completed with per-ticket failures",
  },
  OPEN_PHONE_RECONCILIATION_BACKFILL_REFUSED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "OpenPhone reconciliation backfill refused to write because a gate did not hold",
  },
  REQUEST_FOR_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Request for quote not found for homeowner-responsible amounts message",
  },
  ACTIVE_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Active quote not found for homeowner-responsible amounts message",
  },
  CONVERSATION_PLACEMENT_BATCH_TOO_LARGE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Phone conversation placement batch exceeds the supported size",
  },
  CONVERSATION_BIRTH_TEAM_NOT_ASSIGNABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Phone conversation was born on a fallback team because its decided team was not assignable",
  },
  CONVERSATION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Conversation not found",
  },
  NO_ACTIVE_TASK_CONVERSATION: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No active task conversation for the caller",
  },
  CONVERSATION_CREATION_RACE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Conversation creation lost a concurrent race",
  },
  CONVERSATION_ITEM_CREATION_RACE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Conversation item creation lost a concurrent race",
  },
  MESSAGE_CREATION_RACE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Message creation lost a concurrent race",
  },
  TICKET_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Ticket not found" },
  PROJECT_BID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Project bid not found",
  },
  NO_REACTIONS_FOUND_FOR_MESSAGE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "No reactions found for message",
  },
  MESSAGE_NOT_IN_SOLAR_COMPANY_THREAD: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Message not in solar company thread",
  },
  MESSAGE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Message not found",
  },
  SPECIALIST_LINE_OWNERSHIP_CONFLICT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "Specialist line ownership is not 1:1 — a line with multiple active owners or an owner with multiple lines; resolution picks the oldest deterministically, but routing/attribution for the losers is ambiguous until the data is fixed",
  },
  INTERNAL_LINE_OWNERSHIP_CONFLICT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "One Remi-internal phone number has both active and deactivated owners, so whether the line is dead depends on which duplicate Phone row is read; the send-block rule treats it as live. Feeds the phone-mapping cleanup",
  },
  MESSAGE_DELETE_FORBIDDEN: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Cannot delete audit-tagged escalation message",
  },
  MESSAGE_EDIT_FORBIDDEN: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Cannot edit audit-tagged escalation message",
  },
  NOTIFICATION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Notification not found",
  },
  NOTIFICATION_EVENT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Notification event not found",
  },
  PHONE_NUMBER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Phone number missing",
  },
  PROXY_PHONE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Proxy phone not found",
  },
  PHONE_CALL_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Existing phone call not found",
  },
  PHONE_CALL_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Phone call not found by id",
  },
  OPENPHONE_PHONE_CALL_NOT_FOUND_FOR_TRANSCRIPT: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "OpenPhone phone call not found for transcript",
  },
  TRANSCRIPT_REDACTION_SEGMENT_MISSING: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "PII redaction returned no segment for a transcript segment, so the transcript was not written",
  },
  PHONE_CALL_FAILED_TO_PARSE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Phone call failed to parse",
  },
  PHONE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Phone not found",
  },
  UNATTACHED_PROXY_PHONE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Unattached proxy phone number",
  },
  NO_AVAILABLE_PROXY_PHONES: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No available proxy phone numbers",
  },
  PROXY_ASSIGNMENT_ATTEMPT_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Proxy phone assignment attempt failed",
  },
  PROXY_ASSIGNMENT_HOMEOWNER_SOURCE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Homeowner source blocked from a shared proxy number",
  },
  PROXY_ASSIGNMENT_ORG_UNRESOLVED: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Shared proxy inbound source could not be resolved to an organization",
  },
  PROXY_ASSIGNMENT_NO_ORG_MATCH: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor has no assignment on the dialed shared proxy number",
  },
  PROXY_ASSIGNMENT_HOMEOWNER_PHONE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Shared proxy assignment homeowner phone not found",
  },
  INBOX_VIEW_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Inbox view not found",
  },
  USER_NOT_ALLOWED_TO_UPDATE_INBOX_VIEW: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "User not allowed to update inbox view",
  },
  USER_NOT_ALLOWED_TO_DELETE_INBOX_VIEW: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "User not allowed to delete inbox view",
  },
  ORGANIZATION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Organization not found",
  },
  CONVERSATION_ITEM_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Conversation item not found",
  },
  CONVERSATION_ITEM_WRONG_TYPE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Conversation item wrong type",
  },
  CONVERSATION_WRONG_TYPE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Conversation wrong type",
  },
  CONVERSATION_ITEM_INVALID_STATE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Conversation item invalid state",
  },
  CONVERSATION_ITEM_NOT_REPLYABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Cannot reply to a reply or reaction — replies are one level deep",
  },
  CONVERSATION_ITEM_NOT_REACTABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Cannot react to a reaction — reactions are only allowed on top-level items or replies",
  },
  RELATED_CONVERSATIONS_NOT_IMPLEMENTED_FOR_TYPE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Not implemented for type",
  },
  INVALID_PHONE_PROVIDER: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid phone provider",
  },
  UNKNOWN_NOTIFICATION_TYPE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Unknown notification type",
  },
  WEB_PUSH_NOT_CONFIGURED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Web push was skipped because the VAPID keys or subject are missing or invalid",
  },
  PROJECT_MISSING_PARTNER_FLOW: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Project missing partner flow",
  },
  UNABLE_TO_DETERMINE_AI_SUMMARY_VERSION: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Unable to determine AI summary version",
  },
  WORKSITE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Worksite not found",
  },
  CANNOT_DEACTIVATE_PROXY_PHONE_WITH_PHONE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Cannot deactivate proxy phone with phone",
  },
  CANNOT_DEACTIVATE_PROXY_PHONE_WITH_ACTIVE_ASSIGNMENT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Cannot deactivate proxy phone with an active assignment",
  },
  CANNOT_ASSIGN_DEDICATED_PROXY_TO_NON_CONTRACTOR_PHONE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Cannot assign a dedicated 1:1 proxy to a phone that is not a contractor phone",
  },
  CANNOT_ASSIGN_DEDICATED_PROXY_TO_SHARED_PHONE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Cannot assign a dedicated 1:1 proxy to a phone already served by shared assignments",
  },
  PROXY_PHONE_ALREADY_SERVES_ASSIGNMENTS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Proxy phone already serves shared assignments and cannot be bound 1:1",
  },
  CANNOT_UNASSIGN_PROXY_PHONE_WITH_ACTIVE_ASSIGNMENT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Cannot unassign a proxy phone whose phone carries active shared assignments",
  },
  DEDICATED_PROXY_ASSIGNMENT_LOST_RACE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Proxy phone was claimed or gained an assignment before the dedicated assign landed",
  },
  PROXY_PHONE_ASSIGNMENT_HOMEOWNER_CHANGED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Shared assignment now serves a different homeowner than the one the release targeted",
  },
  PROXY_PHONE_ASSIGNMENT_ALREADY_EXISTS_FOR_ORG: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Proxy phone already serves this contractor org for a different homeowner",
  },
  CONTRACTOR_ALREADY_REACHES_HOMEOWNER_VIA_OTHER_PROXY: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Contractor org already reaches this homeowner phone on a different proxy",
  },
  CANNOT_CREATE_ASSIGNMENT_ON_DEDICATED_PROXY: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Refused a shared assignment on a proxy phone bound 1:1",
  },
  CANNOT_CREATE_ASSIGNMENT_FOR_DEDICATED_PHONE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Refused a shared assignment for a homeowner phone holding a dedicated proxy",
  },
  PROXY_PHONE_ASSIGNMENT_RACED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Shared assignment create hit a unique whose conflicting row was gone by the re-read",
  },
  PROXY_PHONE_ASSIGNMENT_NOT_RELEASED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "End-of-conversation retry asked for an assignment that is not released",
  },
  PROXY_END_OF_CONVERSATION_UNAVAILABLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Release asked to send an end-of-conversation text whose chain does not resolve",
  },
  PROXY_RELEASE_COUNT_MISMATCH: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Stale-bid proxy release count did not match the active assignments found",
  },
  STALE_PROXY_RELEASE_COUNT_MISMATCH: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Stale-proxy cron release count did not match the active assignments found",
  },
  TOO_MANY_ASSIGNMENTS_TO_RELEASE_AT_ONCE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Contractor org held more active assignments than one disable-and-release may scan",
  },
  PROXY_PHONE_DEACTIVATED_WITH_RACED_ASSIGNMENT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Assignment raced in after irreversible proxy-phone deactivation; force-released it",
  },
  GROUP_DETECTED_IN_PROXY_CONVERSATION: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Proxy conversations should not have more than 2 participants",
  },
  TEAM_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Team not found",
  },
  FAILED_TO_SYNC_SMS_MESSAGES: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to sync SMS messages",
  },
  FAILED_TO_CREATE_AI_SUMMARY_MESSAGE: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to create AI summary message",
  },
  NO_BID_FOUND_FOR_ADDRESS: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No bid found for address",
  },
  EMAIL_ADDRESS_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Email address not found for person",
  },
  INVALID_CONVERSATION_KEY: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid conversation key format",
  },
  INVALID_ORGANIZATION_TYPES_FOR_AUTOMATED_MESSAGE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid organization types for automated message",
  },
  CANNOT_BUY_PHONE_NUMBER_IN_NON_PRODUCTION_ENVIRONMENT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Cannot buy phone number in non-production environment",
  },
  NO_SUNRUN_RECORD_ID_FOUND_FOR_WORK_ORDER: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No Sunrun record ID found for work order",
  },
  PROXY_PHONE_NUMBER_USED_AS_CONTACT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Proxy phone number cannot be used as a contact phone number",
  },
  SYSTEM_PHONE_NUMBER_USED_AS_CONTACT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "System phone number cannot be used as a contact phone number",
  },
  TELECOM_USER_ALREADY_MAPPED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Telecom user has already been mapped to a person",
  },
  TELECOM_PERSON_EMAIL_ALREADY_EXISTS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A person with this email already exists during telecom user mapping",
  },
  EMAIL_OBJECT_BUCKET_NOT_CONFIGURED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "AWS_S3_EMAIL_OBJECT_BUCKET_NAME environment variable is not configured",
  },
  WEBHOOK_OWNERS_FILTERED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Owners were filtered out of outbound message webhook",
  },
  USER_HAS_NO_CONVERSATION_ON_BID: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "External user loaded a bid with no conversations — message box will be missing",
  },
  UNAUTHORIZED_CHANNEL_CALLER: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Caller is not authorized to write a delivery receipt for this message",
  },
  PARTNER_MENTION_UNAUTHORIZED_SENDER: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Non-Remi-internal sender attempted to attach partner-mention recipients",
  },
  PARTNER_MENTION_PROJECT_CONVERSATION_MISMATCH: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Partner-mention projectId does not belong to the message's conversation",
  },
  PORTAL_COLOR_SELECTION_DISABLED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Portal color selection feature flag is disabled",
  },
  XA_FILE_DELIVERY_INVALID_INPUT: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription:
      "XA file-delivery message received an empty delivering organization or external ref (pipeline invariant violation — both are required on the event)",
  },
  XA_FILE_DELIVERY_BID_UNRESOLVED: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription: "Bid not yet resolvable for an XA file-delivery message",
  },
  WESTHILL_COMPLETION_SUMMARY_INVALID_INPUT: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription:
      "Westhill completion summary received an empty delivering organization or external ref (pipeline invariant violation — both are required on the event)",
  },
  WESTHILL_COMPLETION_SUMMARY_BID_UNRESOLVED: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription: "Bid not yet resolvable for a Westhill completion summary note",
  },
  WESTHILL_COMPLETION_SUMMARY_PLACEHOLDER_LEAK: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription:
      "Westhill completion summary rendered with an unsubstituted placeholder and was not sent (template invariant violation — the note text is built entirely from substituted values)",
  },
  CONVERSATION_TEAM_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Conversation team is missing or cannot receive assignments",
  },
  AUTOMATED_MENTION_TEAM_NOT_MENTIONABLE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Automated mention skipped: well-known team is not mentionable",
  },
  AUTOMATED_MENTION_LEAD_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Automated mention fell back to team: sales team leadId references a missing person row",
  },
  CS_LINE_NOT_SENDABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "CS line has no telecom provider mapping — cannot send",
  },
  AUTOMATED_SMS_SEND_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription:
      "Automated SMS send returned FAILED (likely A2P/carrier rejection or an undeliverable recipient)",
  },
  INBOX_CURSOR_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid or expired inbox cursor",
  },
  INBOX_BULK_TARGET_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Inbox bulk action target (assignee or team) is invalid or outside the caller's org",
  },
  INBOX_BULK_ROW_REJECTED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Inbox bulk row rejected by the per-row object fence (never surfaced; caught in runBatch)",
  },
  LIFECYCLE_SENDER_HELD_SEND: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "Lifecycle automated SMS could not resolve an AM sending line (no account manager, the AM has no OpenPhone line, or the worksite is site-visit-only and intentionally has no AM); the send is held — never falls back to support — pending manual resend after line provisioning",
  },
  ACCOUNT_MANAGER_SENDER_UNRESOLVED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "An agent-initiated SMS could not resolve the bid's account-manager sending line (no AM could be assigned on first need, or the AM has no OpenPhone line); the send is refused and never falls back to another line",
  },
  RECIPIENT_PHONE_NOT_ON_BID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Recipient phone does not belong to a contact on the specified bid; collapsed shape — missing, cross-bid, and cross-org all return this so a caller cannot probe boundaries",
  },
  INTRO_ROW_NOT_SCHEDULED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "A point-of-contact intro row was created non-SCHEDULED (would dispatch inline, bypassing send-time suppression); the scheduling clamp failed to place it in the future",
  },
  AUTOMATED_SMS_ROW_NOT_SCHEDULED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "An automated lifecycle SMS row was created non-SCHEDULED (would dispatch inline, bypassing send-time suppression); the scheduling clamp failed to place it in the future",
  },
  INBOX_SEARCH_TIMEOUT: {
    classification: ErrorClassification.TIMEOUT_ERROR,
    sentryDescription: "Inbox search query exceeded its MAX_EXECUTION_TIME ceiling",
  },
  INBOX_SEARCH_INPUT_EMPTY: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "A fixed inbox search built no query, so it would have matched nothing",
  },
  CONVERSATION_QUIET_QUERY_TIMEOUT: {
    classification: ErrorClassification.TIMEOUT_ERROR,
    sentryDescription: "Quiet-conversation queue query exceeded its MAX_EXECUTION_TIME ceiling",
  },
  CONVERSATION_QUIET_CURSOR_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Quiet-conversation cursor was malformed, stale, or issued under a different sort",
  },
  CONVERSATION_UNANSWERED_CURSOR_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Unanswered-conversation cursor was malformed, stale, or issued under a different sort",
  },
  CONVERSATION_ITEMS_CURSOR_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Pass the prior response's items.nextCursor or conversation.nextCursor, not a conversationItemId.",
  },
  CONVERSATION_HISTORY_CURSOR_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Pass the prior response's history.nextCursor, or nextCursor on a ticket activity response. Do not pass a conversationItemId.",
  },
  TELECOM_PHONE_NUMBER_NOT_ACTIVE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "That number is not an active OpenPhone line",
  },
  TELECOM_PERSON_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Person not found for telecom number assignment",
  },
  TELECOM_PERSON_NOT_INTERNAL: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Only Remi-internal people can be assigned an OpenPhone number",
  },
  TELECOM_PERSON_MISSING_CONTACT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "This person has no contact record to attach the number to",
  },
  TELECOM_PERSON_ALREADY_HAS_LINE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "This person already has an OpenPhone line — unlink it before assigning a new one",
  },
  TELECOM_SEAT_OWNED_BY_OTHER: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "That telecom user is already assigned to another person",
  },
  TELECOM_USER_NOT_ON_NUMBER: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "That telecom user is not on the selected OpenPhone number",
  },
  TELECOM_OPEN_PHONE_UNAVAILABLE: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Could not reach OpenPhone to verify the number — try again",
  },
  TELECOM_LINE_TEAM_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "This OpenPhone line has no owning team — an admin sets the line's team first",
  },
  TELECOM_LINE_TEAM_MEMBERSHIP_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "This person is not a live member of the team that owns this OpenPhone line",
  },
  TELECOM_LINE_PLACEHOLDER_REPAIR_REFUSED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "The OpenPhone placeholder-owner repair refused to write",
  },
  TELECOM_LINE_TEAM_NOT_ASSIGNABLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "That team cannot own a phone line — it is deleted or cannot be assigned conversations",
  },
  TELECOM_LINE_DUPLICATE_ROWS: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "An OpenPhone number carries more than one line row — routing reads only the newest, so a team set on an older row is silently ignored",
  },
  INBOX_COUNTS_UNSUPPORTED_ASSIGNEE_SHAPE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Batched inbox counts rejected an assignee filter it cannot scope to the caller; only an equality against a single person id is supported, so team filters, multi-person filters, Unassigned, and any assignee condition reachable through an OR are refused rather than silently narrowed to the caller",
  },
  INBOX_COUNTS_MISSING_ASSIGNEE_ANCHOR: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Batched inbox counts requires every definition block to filter on a single assignee; a block without one would scan unanchored and cache under a shared key",
  },
  CONVERSATION_UNASSIGN_NOT_PERMITTED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "A conversation reassignment asked for a null assignee while expecting a non-null one - the null target exists only for team-only writes and must never clear an assignee",
  },
  ACCOUNT_MANAGER_STAFFING_BID_MISSING: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription:
      "Staff-on-demand resolved a project from a live bid link and the engine then reported BID_NOT_FOUND, so the link broke mid-flight",
  },
  CONVERSATION_OWNERSHIP_NOT_MANAGED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "An ownership unpin named a conversation outside the partner channels whose ownership is state-derived - reconcile applies no channel filter of its own, so an unscoped id would derive a partner-flow owner for a thread that has none",
  },
  ESCALATION_SEAT_ASSUMPTION_ON_LIVE_RUN: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "The escalation seat writer was asked to assume a seat or cleared pins on a run that writes; assumptions exist only so a dry run can preview what the launch apply will create",
  },
  CONVERSATION_OWNERSHIP_SWEEP_CURSOR_STALLED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription:
      "The ownership sweep's keyset cursor stopped advancing, so a page repeated instead of the walk completing",
  },
  INBOX_FILTER_SELECT_CONFLICT: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription:
      "The inbox lazy-join filter registry triggered two fields that both declare a select projection; the second would silently overwrite the first, so the compile refuses instead",
  },
  INBOX_COUNTS_HETEROGENEOUS_BATCH: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Batched inbox counts rejected a batch whose definitions do not share one SQL shape per entity; grouping them into a single statement would change the joins, drop a HAVING clause, or lose a DISTINCT and silently miscount",
  },
  // Must stay distinct from BID_NOT_FOUND: a retry classifier gates this identity alone, and merging
  // them would truncate the retry budget of every other step it wraps.
  PRE_BID_CONVERSATION_LINK_BID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Bid not found while linking pre-existing homeowner conversations",
  },
  CANNOT_ASSIGN_PROXY_TO_INTERNAL_LINE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Proxy phone assignment blocked because the target phone is an internal Remi line (has a TelecomProviderPhone record)",
  },
  SMS_MESSAGE_NOT_RESCHEDULABLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Reschedule compare-and-swap updated no row",
  },
};
